import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageContainer from '../../components/common/PageContainer/PageContainer';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Button from '../../components/common/Button/Button';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Badge from '../../components/common/Badge/Badge';
import Loading from '../../components/common/Loading/Loading';
import {
  PortalUserProfileCredentialsSection,
  PortalUserProfileLayout,
  PortalUserProfileLeftHeader,
  PortalUserProfileMetaSection,
  PortalUserProfileMetaValue,
  PortalUserProfilePhotoSection,
  type PortalUserCredentialsErrors,
  type PortalUserCredentialsField,
  type PortalUserCredentialsValues,
} from '../../components/common/PortalUserProfileForm';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { AccountService } from '../../services/AccountService';
import { PORTAL_ROLE_LABELS } from '../../types/portalUser';
import styles from './AccountSettings.module.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_MAX_LENGTH = 100;

const EMPTY_CREDENTIALS: PortalUserCredentialsValues = {
  fullName: '',
  email: '',
  currentPassword: '',
  password: '',
  confirmPassword: '',
};

const AccountSettings: React.FC = () => {
  const { user } = useAuth();
  const { setBreadcrumbs } = useBreadcrumbs();

  const [credentials, setCredentials] = useState<PortalUserCredentialsValues>(EMPTY_CREDENTIALS);
  const [initialCredentials, setInitialCredentials] = useState<PortalUserCredentialsValues>(EMPTY_CREDENTIALS);
  const [roles, setRoles] = useState<Array<keyof typeof PORTAL_ROLE_LABELS>>([]);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<PortalUserCredentialsErrors>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    setBreadcrumbs([{ label: 'Minha conta' }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    let isMounted = true;

    const loadAccount = async () => {
      setLoading(true);
      setSubmitError('');

      try {
        const account = await AccountService.getCurrentAccount();

        if (!isMounted) {
          return;
        }

        const hydratedCredentials: PortalUserCredentialsValues = {
          fullName: account.full_name,
          email: account.email,
          currentPassword: '',
          password: '',
          confirmPassword: '',
        };

        setCredentials(hydratedCredentials);
        setInitialCredentials(hydratedCredentials);
        setRoles(account.roles);
        setIsActive(account.is_active);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        const message = error instanceof Error ? error.message : 'Não foi possível carregar os dados da conta.';
        setSubmitError(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (user) {
      void loadAccount();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [user]);

  const isDirty = useMemo(
    () => credentials.fullName !== initialCredentials.fullName
      || credentials.email !== initialCredentials.email
      || Boolean(credentials.currentPassword || credentials.password || credentials.confirmPassword),
    [credentials, initialCredentials],
  );

  const validate = useCallback((): boolean => {
    const nextErrors: PortalUserCredentialsErrors = {};
    const normalizedCurrentPassword = credentials.currentPassword.trim();
    const normalizedPassword = credentials.password.trim();
    const normalizedConfirmPassword = credentials.confirmPassword.trim();
    const shouldValidatePassword = Boolean(
      normalizedCurrentPassword || normalizedPassword || normalizedConfirmPassword,
    );

    if (!credentials.fullName.trim()) {
      nextErrors.fullName = 'Nome completo é obrigatório.';
    }

    if (!credentials.email.trim()) {
      nextErrors.email = 'E-mail é obrigatório.';
    } else if (!EMAIL_REGEX.test(credentials.email.trim())) {
      nextErrors.email = 'Informe um e-mail válido.';
    }

    if (shouldValidatePassword) {
      if (!normalizedCurrentPassword) {
        nextErrors.currentPassword = 'Senha atual é obrigatória para alterar a senha.';
      }

      if (!normalizedPassword) {
        nextErrors.password = 'Nova senha é obrigatória.';
      } else if (normalizedPassword.length < PASSWORD_MIN_LENGTH) {
        nextErrors.password = `A senha deve ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`;
      } else if (normalizedPassword.length > PASSWORD_MAX_LENGTH) {
        nextErrors.password = `A senha deve ter no máximo ${PASSWORD_MAX_LENGTH} caracteres.`;
      }

      if (!normalizedConfirmPassword) {
        nextErrors.confirmPassword = 'Confirmar senha é obrigatório.';
      } else if (normalizedPassword !== normalizedConfirmPassword) {
        nextErrors.confirmPassword = 'As senhas precisam ser iguais.';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [credentials]);

  const handleCredentialsChange = (field: PortalUserCredentialsField, value: string) => {
    setCredentials((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
    setSuccessMessage('');
  };

  const handleReset = () => {
    setCredentials(initialCredentials);
    setErrors({});
    setSubmitError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    setSuccessMessage('');

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      const normalizedPassword = credentials.password.trim();
      const message = await AccountService.updateCurrentAccount({
        fullName: credentials.fullName.trim(),
        email: credentials.email.trim().toLowerCase(),
        password: normalizedPassword || undefined,
        currentPassword: normalizedPassword ? credentials.currentPassword.trim() : undefined,
      });

      const nextCredentials: PortalUserCredentialsValues = {
        fullName: credentials.fullName.trim(),
        email: credentials.email.trim().toLowerCase(),
        currentPassword: '',
        password: '',
        confirmPassword: '',
      };

      setCredentials(nextCredentials);
      setInitialCredentials(nextCredentials);
      setSuccessMessage(message);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível atualizar a conta.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className={styles.root}>
        <PageHeader
          title="Minha conta"
          subtitle="Gerencie seus dados de acesso, foto de perfil e credenciais"
        />

        {successMessage && <AlertBanner variant="success" message={successMessage} className={styles.alert} />}
        {submitError && <AlertBanner variant="error" message={submitError} className={styles.alert} />}

        <PortalUserProfileLayout
          onSubmit={handleSubmit}
          leftColumn={(
            <>
              <PortalUserProfileLeftHeader />
              <PortalUserProfilePhotoSection userId={user?.id} disabled={submitting} />
              <PortalUserProfileMetaSection label="Perfis">
                {roles.length > 0 ? (
                  roles.map((role) => (
                    <Badge key={role} variant="info">
                      {PORTAL_ROLE_LABELS[role]}
                    </Badge>
                  ))
                ) : (
                  <PortalUserProfileMetaValue>Nenhum perfil vinculado</PortalUserProfileMetaValue>
                )}
              </PortalUserProfileMetaSection>
              <PortalUserProfileMetaSection label="Status da conta">
                <PortalUserProfileMetaValue>{isActive ? 'Ativa' : 'Inativa'}</PortalUserProfileMetaValue>
              </PortalUserProfileMetaSection>
            </>
          )}
          rightColumn={(
            <PortalUserProfileCredentialsSection
              idPrefix="account"
              variant="account"
              values={credentials}
              errors={errors}
              disabled={submitting}
              onChange={handleCredentialsChange}
            />
          )}
          actions={(
            <>
              <Button
                type="button"
                variant="outline"
                size="small"
                onClick={handleReset}
                disabled={submitting || !isDirty}
              >
                Desfazer alterações
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="small"
                disabled={submitting || !isDirty}
              >
                {submitting ? 'Salvando...' : 'Salvar alterações'}
              </Button>
            </>
          )}
        />
      </div>
    </PageContainer>
  );
};

export default AccountSettings;
